import React, { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { getRequestURL } from "@tanstack/react-start/server";
import { z } from "zod";
import { getStripe } from "~/lib/stripe";
import { ensureStripeCustomer } from "~/lib/stripe/customer";
import { ChevronLeft } from "lucide-react";
import { Button } from "~/components/ui/button";
import { getCoach, updateCoach } from "~/lib/server/coach";
import { MembershipPlan } from "~/lib/schemas";
import { toast } from "sonner";
import { PlanBadge } from "~/components/plan-badge";
import { cn } from "~/lib/cn";
import Heading from "~/components/ui/heading";
import { Card } from "~/components/ui/card";
import type Stripe from "stripe";
import { MembershipPlanDetails } from "~/components/membership-plan-details";
import { SavingsComparisonTable } from "~/components/savings-comparison-table";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { getAppUrl } from "~/lib/utils";

interface PlanPrice {
  id: string;
  amount: number;
  interval: string;
  currency: string;
}

interface PlanPrices {
  monthly?: PlanPrice;
  yearly?: PlanPrice;
}

export const getStripePrices = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async () => {
    const stripe = await getStripe();
    const prices: Record<MembershipPlan, PlanPrices> = {
      [MembershipPlan.Standard]: {},
      [MembershipPlan.Pro]: {},
      [MembershipPlan.ProPlus]: {},
    };

    try {
      const stripePrices = await stripe.prices.list({
        active: true,
        lookup_keys: [
          "default_pro",
          "default_pro_yearly",
          "default_pro_plus",
          "default_pro_plus_yearly",
        ],
        expand: ["data.product"],
      });

      stripePrices.data.forEach((price) => {
        const lookupKey = price.lookup_key;
        if (!lookupKey || !price.unit_amount) {
          return;
        }

        const priceData = {
          id: price.id,
          amount: price.unit_amount / 100,
          interval: price.recurring?.interval || "month",
          currency: price.currency.toUpperCase(),
        };

        if ("default_pro" === lookupKey) {
          prices[MembershipPlan.Pro].monthly = priceData;
        } else if ("default_pro_yearly" === lookupKey) {
          prices[MembershipPlan.Pro].yearly = priceData;
        } else if ("default_pro_plus" === lookupKey) {
          prices[MembershipPlan.ProPlus].monthly = priceData;
        } else if ("default_pro_plus_yearly" === lookupKey) {
          prices[MembershipPlan.ProPlus].yearly = priceData;
        }
      });
    } catch {
      // Ignore fetch errors - return empty prices array
    }

    return prices;
  });

export const createReferralCoupon = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    if (!coach?.referredByCoachId) {
      return null;
    }

    const stripe = await getStripe();

    // Create a one-time use coupon
    const coupon = await stripe.coupons.create({
      amount_off: 5000, // $50 off
      currency: "usd",
      name: "Referral Discount",
      max_redemptions: 1,
      metadata: {
        coachId: coach.id,
        referredByCoachId: coach.referredByCoachId,
      },
    });

    return coupon.id;
  });

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(
    z.object({
      priceId: z.string(),
    }),
  )
  .handler(async ({ data, context }) => {
    const { priceId } = data;
    const coach = context.coach;
    if (!coach) {
      throw new Error("Coach not found");
    }

    const stripe = await getStripe();
    const requestUrl = getRequestURL();
    const appUrl = getAppUrl({ url: requestUrl.href } as Request);

    // Get or create Stripe customer
    const customerId = await ensureStripeCustomer(coach);
    if (!customerId) {
      throw new Error("Failed to create or retrieve Stripe customer");
    }
    // TypeScript should now understand customerId is string
    const validCustomerId: string = customerId;

    // Create referral coupon if coach was referred
    let couponId: string | null = null;
    if (coach.referredByCoachId) {
      couponId = await createReferralCoupon();
    }

    // Check if Stripe already has a subscription for this coach
    let subscription: Stripe.Subscription | null = null;
    if (coach.stripeSubscriptionId) {
      const subscriptionData = await stripe.subscriptions.retrieve(
        coach.stripeSubscriptionId,
      );
      // Ensure we have the actual subscription object
      subscription = subscriptionData as unknown as Stripe.Subscription;
    } else {
      const s = await stripe.subscriptions.list({
        customer: validCustomerId,
        status: "active",
      });
      subscription = 0 < s.data.length ? s.data[0] : null;

      if (subscription) {
        await updateCoach(coach.id, {
          stripeSubscriptionId: subscription.id,
        });
      }
    }

    if (subscription) {
      // Get subscription item
      const subscriptionItems = await stripe.subscriptionItems.list({
        subscription: subscription.id,
      });
      const subscriptionItem = subscriptionItems.data[0];

      if (priceId) {
        // Create session to billing portal
        const session = await stripe.billingPortal.sessions.create({
          customer: validCustomerId,
          return_url: `${appUrl}/coach/membership-plans`,
          flow_data: {
            type: "subscription_update_confirm",
            subscription_update_confirm: {
              items: [
                {
                  id: subscriptionItem.id,
                  price: priceId,
                },
              ],
              subscription: subscription.id,
            },
            after_completion: {
              type: "redirect",
              redirect: {
                return_url: `${appUrl}/coach/membership-plans?success=true&coachId=${coach.id}`,
              },
            },
          },
        });
        return session.url;
      }
      // Go to billing portal to cancel
      const session = await stripe.billingPortal.sessions.create({
        customer: validCustomerId,
        return_url: `${appUrl}/coach/membership-plans`,
        flow_data: {
          type: "subscription_cancel",
          subscription_cancel: {
            subscription: subscription.id,
          },
        },
      });
      return session.url;
    }

    // Create checkout session
    if (!priceId) {
      throw new Error("Price ID is required for checkout");
    }
    // TypeScript should now know priceId is string, but let's be explicit
    const validPriceId: string = priceId;

    const session = await stripe.checkout.sessions.create({
      customer: validCustomerId,
      line_items: [
        {
          price: validPriceId,
          quantity: 1,
        },
      ],
      mode: "subscription",
      ...(couponId ? { discounts: [{ coupon: couponId }] } : {}),
      success_url: `${appUrl}/coach/membership-plans?success=true&coachId=${coach.id}`,
      cancel_url: `${appUrl}/coach/membership-plans?canceled=true`,
      metadata: {
        coachId: coach.id,
        ...(coach.referredByCoachId
          ? { referredByCoachId: coach.referredByCoachId }
          : {}),
      },
    });

    return session.url;
  });

export const createPortalSession = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    if (!coach || !coach.stripeCustomerId) {
      throw new Error("Coach not found or no Stripe customer");
    }

    const stripe = await getStripe();
    const requestUrl = getRequestURL();
    const appUrl = getAppUrl({ url: requestUrl.href } as Request);
    const session = await stripe.billingPortal.sessions.create({
      customer: coach.stripeCustomerId,
      return_url: `${appUrl}/coach/membership-plans`,
    });

    return session.url;
  });

export const updateSubscriptionFromCheckout = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(
    z.object({
      coachId: z.string(),
    }),
  )
  .handler(async ({ data }) => {
    const { coachId } = data;
    const stripe = await getStripe();
    const coach = await getCoach(coachId);

    if (!coach) {
      throw new Error("Coach not found");
    }

    let customerId: string;

    if (!coach.stripeCustomerId) {
      // Find the customer in Stripe based on coachId in metadata
      const customers = await stripe.customers.search({
        limit: 1,
        query: `metadata['coachId']:'${coachId}'`,
      });

      const customer = 0 < customers.data.length && customers.data[0];
      if (!customer) {
        throw new Error("Customer not found");
      }

      // Update coach's customer ID if not set
      await updateCoach(coach.id, {
        stripeCustomerId: customer.id,
      });

      customerId = customer.id;
    } else {
      customerId = coach.stripeCustomerId!; // Non-null assertion since we've checked above
    }

    // Get the customer's active subscription
    const subscriptions = await stripe.subscriptions.list({
      customer: customerId,
      status: "active",
    });

    await Promise.all(
      subscriptions.data.map(async (subscription) => {
        // Get the product details to determine the plan type
        const product = await stripe.products.retrieve(
          subscription.items.data[0].price.product as string,
        );

        const planType = {
          pro: MembershipPlan.Pro,
          pro_plus: MembershipPlan.ProPlus,
        }[product.metadata.plan_type];

        if (!planType) {
          return;
        }

        // Update coach with new subscription details
        await updateCoach(coach.id, {
          stripeSubscriptionId: subscription.id,
          membershipPlan: planType,
          subscriptionStatus: subscription.status,
        });
      }),
    );

    return { success: true };
  });

export const Route = createFileRoute("/coach/_backend/membership-plans/")({
  validateSearch: z.object({
    success: z.string().optional(),
    coachId: z.string().optional(),
  }),
  head: () => ({
    title: "Membership Plans – Dream Coach Match",
    meta: [
      {
        name: "description",
        content:
          "Choose the perfect coaching plan for your business. From free to premium features.",
      },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["stripe-prices"],
      queryFn: () => getStripePrices(),
    });
    return {};
  },
  component: MembershipPlansPage,
});

function MembershipPlansPage() {
  const { data: prices } = useQuery({
    queryKey: ["stripe-prices"],
    queryFn: () => getStripePrices(),
  });

  const [loading, setLoading] = useState<string | null>(null);
  const [isYearly, setIsYearly] = useState(true);
  const coach = useCoachPrivateProfileRequired();
  const search = Route.useSearch();
  const queryClient = useQueryClient();

  const createCheckoutMutation = useMutation({
    mutationFn: (priceId: string) =>
      createCheckoutSession({ data: { priceId } }),
    onSuccess: (url) => {
      if (url) {
        (window.top ?? window).location.href = url;
      } else {
        toast.error("Failed to start checkout");
      }
    },
    onError: () => {
      toast.error("Failed to start checkout");

      setLoading(null);
    },
  });

  const createPortalMutation = useMutation({
    mutationFn: () => createPortalSession(),
    onSuccess: (url) => {
      if (url) {
        (window.top ?? window).location.href = url;
      } else {
        toast.error("Failed to open billing portal");
      }
    },
    onError: () => {
      toast.error("Failed to open billing portal");
    },
    onSettled: () => setLoading(null),
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: (coachId: string) =>
      updateSubscriptionFromCheckout({ data: { coachId } }),
    onSuccess: () => {
      toast.success("Successfully updated plan!");
      queryClient.invalidateQueries({ queryKey: ["stripe-prices"] });
    },
    onError: () => {
      toast.error(
        "There was an issue updating your subscription. Please contact support if this persists.",
      );
    },
  });

  // Handle successful purchase
  useEffect(() => {
    const success = search.success;
    const coachId = search.coachId;

    if ("true" === success && coachId) {
      updateSubscriptionMutation.mutate(coachId);
    }
  }, [search, updateSubscriptionMutation]);

  const handleSubscribe = (plan: MembershipPlan) => {
    if (loading || !prices) {
      return;
    }
    setLoading(plan);

    const planPrices = prices[plan];
    const priceId = isYearly ? planPrices.yearly?.id : planPrices.monthly?.id;

    if (!priceId) {
      toast.error("Pricing not available for this plan");
      setLoading(null);
      return;
    }

    createCheckoutMutation.mutate(priceId!);
  };

  const handleManageSubscription = () => {
    if (loading) {
      return;
    }
    setLoading("manage");
    createPortalMutation.mutate();
  };

  const Check = (
    props: React.SVGProps<SVGSVGElement> & { className?: string },
  ) => {
    return (
      <div
        className={cn(
          "bg-brand-100 flex h-5 w-5 shrink-0 grow-0 items-center justify-center gap-2 rounded-full",
          props.className,
        )}
      >
        <Check
          {...props}
          className={cn("text-brand-700", props.className, "h-3 w-3")}
        />
      </div>
    );
  };

  const SubscribeButton = ({
    plan,
    variant = "default",
    className,
  }: {
    plan: MembershipPlan;
    variant?: "outline" | "fancy" | "default";
    className?: string;
  }) => {
    if (!coach) {
      return null;
    }

    const isDowngrade =
      plan === MembershipPlan.Standard ||
      (coach.membershipPlan === MembershipPlan.ProPlus &&
        plan !== MembershipPlan.ProPlus);
    const isCurrentPlan = coach.membershipPlan === plan;
    const isUpgrade =
      plan === MembershipPlan.ProPlus &&
      coach.membershipPlan === MembershipPlan.Pro;
    const hasSubscription = Boolean(coach.stripeSubscriptionId);

    if (isDowngrade && hasSubscription) {
      return (
        <Button
          onClick={() => handleSubscribe(plan)}
          variant="destructive-ghost"
          className={cn("w-full", className)}
          disabled={loading === plan}
        >
          {loading === plan ? "Loading..." : "Downgrade"}
        </Button>
      );
    }

    if (isCurrentPlan && hasSubscription) {
      return (
        <Button
          onClick={handleManageSubscription}
          variant="outline"
          className={cn("w-full", className)}
          disabled={"manage" === loading}
        >
          {"manage" === loading ? "Loading..." : "Manage Subscription"}
        </Button>
      );
    }

    if (plan !== MembershipPlan.Standard) {
      return (
        <Button
          onClick={() => handleSubscribe(plan)}
          variant={variant}
          className={cn("w-full", className)}
          disabled={loading === plan}
        >
          {loading === plan
            ? "Loading..."
            : isUpgrade
              ? "Upgrade"
              : "Subscribe"}
        </Button>
      );
    }

    return null;
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPriceDisplay = (plan: MembershipPlan) => {
    if (!prices) {
      return null;
    }

    const planPrices = prices[plan];

    if (isYearly) {
      const yearlyPrice = planPrices.yearly;
      const monthlyPrice = planPrices.monthly;
      if (!yearlyPrice || !monthlyPrice) {
        return null;
      }

      const monthlyEquivalent = yearlyPrice.amount / 12;
      const savings =
        ((monthlyPrice.amount * 12 - yearlyPrice.amount) /
          (monthlyPrice.amount * 12)) *
        100;

      return (
        <div className="mt-4 flex flex-col items-start justify-start">
          <div className="text-3xl font-bold">
            {formatPrice(monthlyEquivalent, yearlyPrice.currency)}
            <span className="text-text-tertiary text-base font-normal">
              /month
            </span>
          </div>
          <div className="mt-1 text-sm">
            <span className="text-gray-500 line-through">
              {formatPrice(monthlyPrice.amount, monthlyPrice.currency)}/mo
            </span>
            <span className="ml-2 text-emerald-600">
              Save {Math.round(savings)}%
            </span>
          </div>
          <div className="text-xs text-gray-500">
            billed as {formatPrice(yearlyPrice.amount, yearlyPrice.currency)}
            /year
          </div>
        </div>
      );
    }

    const monthlyPrice = planPrices.monthly;
    if (!monthlyPrice) {
      return null;
    }

    return (
      <div className="mt-4 text-3xl font-bold">
        {formatPrice(monthlyPrice.amount, monthlyPrice.currency)}
        <span className="text-text-tertiary text-base font-normal">/month</span>
      </div>
    );
  };

  if (!prices) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-4">
      <Link
        to="/coach/edit-profile"
        className="text-text-tertiary mb-4 flex items-center gap-2 text-sm"
      >
        <ChevronLeft />
        <span>Back to Edit Profile</span>
      </Link>
      <Heading
        preHeadline="A plan for everyone"
        headline="Membership Plans"
        subheadline="Choose the plan that best fits your needs. All plans include access to our core features, with additional benefits as you scale."
        className="my-16"
      />
      {coach.referredByCoachId && (
        <div className="bg-brand-50 text-brand-900 mb-8 rounded-lg p-4">
          <p className="text-sm">
            <span className="font-medium">Special Offer:</span> As a referred
            coach, you'll get a $50 discount on your first Pro plan
            subscription! The discount will be automatically applied at
            checkout.
          </p>
        </div>
      )}
      <div className="mt-8 flex justify-center">
        <div
          className={cn(
            "flex items-center gap-3 rounded-full p-1 transition-colors",
            !isYearly ? "bg-gray-100" : "bg-brand-100",
          )}
        >
          <span
            className={cn(
              "rounded-full px-4 py-2",
              !isYearly && "bg-white shadow",
            )}
          >
            Monthly
          </span>
          <button
            onClick={() => setIsYearly(!isYearly)}
            className={cn(
              "relative h-6 w-11 rounded-full transition-colors",
              isYearly ? "bg-brand-600" : "bg-gray-200",
            )}
          >
            <div
              className={cn(
                "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform",
                isYearly && "translate-x-5",
              )}
            />
          </button>
          <span
            className={cn(
              "rounded-full px-4 py-2",
              isYearly && "bg-white shadow",
            )}
          >
            Yearly
          </span>
        </div>
      </div>
      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        {/* Standard Plan */}
        <Card className="flex flex-col items-stretch p-6">
          <PlanBadge
            membershipPlan={MembershipPlan.Standard}
            className="self-start"
          />
          <p className="mt-4 text-3xl font-bold">Free</p>
          <p className="mt-2 text-gray-600">
            Perfect for getting started with basic coaching features
          </p>
          <ul className="mt-6 grow space-y-3">
            <li className="flex items-center gap-2">
              <Check />
              <span>Coaching profile with 1 listed service</span>
            </li>
            <li className="flex items-center gap-2">
              <Check />
              <span>Share up to 2 content pieces</span>
            </li>
            <li className="flex items-center gap-2">
              <Check />
              <span>CRM for up to 50 contacts</span>
            </li>
            <li className="flex items-center gap-2">
              <Check />
              <span>1:1 emailing with prospects</span>
            </li>
            <li className="flex items-center gap-2">
              <Check />
              <span>Integrated booking & payments</span>
            </li>
          </ul>
          <div className="mt-6">
            <SubscribeButton plan={MembershipPlan.Standard} />
          </div>
        </Card>

        {/* Pro Plan */}
        <Card>
          <div className="flex flex-col items-stretch rounded-lg bg-white/95 p-6">
            <PlanBadge
              membershipPlan={MembershipPlan.Pro}
              className="self-start"
            />
            {getPriceDisplay(MembershipPlan.Pro)}
            <p className="mt-2 text-gray-600">
              Advanced features for growing your coaching business
            </p>
            <ul className="mt-6 grow space-y-3">
              <li className="flex items-center gap-2">
                <Check />
                <span>Everything in Standard</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>Expanded profile with video branding</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>Landing page with VSL</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>Automated follow-ups & texting</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>CRM for up to 5,000 contacts</span>
              </li>
            </ul>
            {prices[MembershipPlan.Pro] && (
              <div className="mt-6">
                <SubscribeButton plan={MembershipPlan.Pro} />
              </div>
            )}
          </div>
        </Card>

        {/* Pro Plus Plan */}
        <Card
          className={cn(
            coach.membershipPlan === MembershipPlan.Pro &&
              "border-gradient-pro-purple -m-1 rounded-xl p-1 shadow-sm",
          )}
        >
          <div className="flex flex-col items-stretch rounded-lg bg-white/95 p-6">
            <PlanBadge
              membershipPlan={MembershipPlan.ProPlus}
              className="self-start"
            />
            {getPriceDisplay(MembershipPlan.ProPlus)}
            <p className="mt-2 text-gray-600">
              Premium features for established coaches
            </p>
            <ul className="mt-6 grow space-y-3">
              <li className="flex items-center gap-2">
                <Check />
                <span>Everything in Pro</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>Multiple coaching offers</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>Host your own website</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>Unlimited CRM contacts</span>
              </li>
              <li className="flex items-center gap-2">
                <Check />
                <span>Create your own communities</span>
              </li>
            </ul>
            {prices[MembershipPlan.ProPlus] && (
              <div className="mt-6">
                <SubscribeButton plan={MembershipPlan.ProPlus} />
              </div>
            )}
          </div>
        </Card>
      </div>
      <Heading
        preHeadline="Look how much you're saving"
        headline="Savings Comparison"
        subheadline="Our comprehensive software can replace your current CRM, email marketing tool, online course platform, and landing page builder."
        className="mt-24"
      />
      <div className="mt-8">
        <SavingsComparisonTable />
      </div>
      {/* White Glove Service */}
      <div className="mt-16">
        <Card className="p-6">
          <div className="grid grid-cols-3 gap-8">
            <div>
              <h2 className="font-heading text-text-primary text-2xl">
                Partner for Growth
              </h2>
              <p className="mt-2 text-lg font-bold">
                Done-with-You Client Acquisition
              </p>
              <p className="text-text-secondary mt-2 text-sm">
                Together with you we build and manage a comprehensive system to
                acquire coaching clients for you.
              </p>
              <div className="mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    const emailLink = document.createElement("a");
                    emailLink.href = "mailto:info@dreamcoachmatch.com";
                    emailLink.click();
                  }}
                >
                  Apply Now
                </Button>
                <p className="text-text-tertiary mt-2 text-xs">
                  We only take 3-4 clients per month to maintain the highest
                  quality.
                </p>
              </div>
            </div>
            <div className="col-span-2">
              <ul className="grid grid-cols-2 gap-4">
                <li className="flex items-center gap-2">
                  <Check />
                  <span>Strategic market analysis</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check />
                  <span>Funnel design & development</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check />
                  <span>Lead nurturing workflows</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check />
                  <span>Sales process support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check />
                  <span>Business scaling support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check />
                  <span>Talent acquisition guidance</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
      {/* Detailed Features Comparison */}
      <div className="mt-12">
        <MembershipPlanDetails />
      </div>
    </div>
  );
}
